import { toast } from "react-toastify";
import AppLoading from "../AppLoading";
import Chart from "react-google-charts";
import downloadCSV from "../../helpers/downloadData";
import { dashboardGraphsOptions } from "../../consts/graphs";
import { ChevronRight, ChevronDown, Plus, X } from "lucide-react";
import { selectedDataRowForPieDefaultState } from "../../helpers/page3";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";

type SubItemType = {
  name: string,
  completed: number,
  noOfCompleted: number,
  incomplete: number,
  noOfIncomplete: number,
  total: number,
};

type SubItemWithFields = SubItemType & {
  [key: string]: any; // For dynamic ID fields like courseId, organisationId, etc.
  countedFields?: SubItemType[]
};

type DataType = {
  total: number,
  subItems: SubItemWithFields[],
}

// API function types
type FetchMainDataFn = (params: Record<string, unknown>, signal?: AbortSignal) => Promise<{ data: SubItemWithFields[] }>;
type FetchSubDataFn = (params: Record<string, unknown>, signal?: AbortSignal) => Promise<{ data: SubItemType[] }>;

// Configuration for drill types with ID fields
type DrilldownTypeConfig = {
  label: string;
  value: string;
  // Field name in the data (e.g., 'courseId', 'organisationId')
  fieldName: string;
  // Parameter name to pass to sub-data API (e.g., 'parentId')
  paramName: string;
  // Type value to pass to sub-data API (e.g., 'Course', 'Organisation')
  typeValue: string;
};

interface DrillDownV2Props {
  total: number;
  filters?: Record<string, unknown>;
  // API functions
  fetchMainData: FetchMainDataFn;
  fetchSubData?: FetchSubDataFn;
  // Drilldown types configuration - combines drill type and ID field config
  drilldownTypes?: DrilldownTypeConfig[];
  // Optional: Custom pie chart data formatter
  onRowHover?: (
    e: React.MouseEvent<HTMLSpanElement>,
    data: Omit<SubItemType, 'name'>,
    setHoveredRowStyle: React.Dispatch<React.SetStateAction<{ top?: number; left?: number } | null>>,
    setSelectedDataRowForPie: React.Dispatch<React.SetStateAction<(string | number)[][]>>,
    setExtraDataForPie: React.Dispatch<React.SetStateAction<Record<string, number | string>>>
  ) => void;
  // Optional: Export filename prefix
  exportFilenamePrefix?: string;
  // Optional: Enable/disable hover pie chart
  enableHoverPieChart?: boolean;
}

// Extract inline styles to constants
const styles = {
  buttonContainer: { display: 'flex', gap: '10px', alignItems: 'center' },
  exportButton: { fontSize: '12px', padding: '0 8px', width: 'auto', minWidth: '90px' },
  trayContainer: { 
    overflowY: 'auto' as const, 
    maxHeight: '320px',
    position: 'absolute' as const,
  },
  trayContainerWithPadding: { 
    overflowY: 'auto' as const, 
    maxHeight: '320px',
    position: 'absolute' as const,
    paddingBottom: '6px'
  },
  hoveredChart: { top: 100, right: -200, position: 'absolute' as const },
  closeButton: {
    backgroundSize: '13px',
    backgroundImage: `url('${process.env.REACT_APP_PUBLIC_URL || process.env.PUBLIC_URL || ''}/close.png')`,
  },
  dropdownItem: { padding: 10 },
  exportHeader: { 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center',
    padding: '12px',
    borderBottom: '2px solid #08305D',
    backgroundColor: '#f5f5f5',
    position: 'sticky' as const,
    top: 0,
    zIndex: 10
  },
  checkboxLabel: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' },
  checkbox: { cursor: 'pointer', width: '16px', height: '16px' },
  exportButtonStyle: {
    padding: '6px 12px',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 500
  },
  categoryHeader: { display: 'flex', alignItems: 'center', gap: '8px' },
  loadingSubItem: { padding: '10px', textAlign: 'center' as const, color: '#666' },
};

// Default row hover handler
const defaultOnRowHover = (
  e: React.MouseEvent<HTMLSpanElement>,
  data: Omit<SubItemType, 'name'>,
  setHoveredRowStyle: React.Dispatch<React.SetStateAction<{ top?: number; left?: number } | null>>,
  setSelectedDataRowForPie: React.Dispatch<React.SetStateAction<(string | number)[][]>>,
  setExtraDataForPie: React.Dispatch<React.SetStateAction<Record<string, number | string>>>
) => {
  if (data) {
    setSelectedDataRowForPie([
      ["Courses", "Percentages"],
      ["Complete %", +(data?.completed || 0)],
      ["Incomplete %", +(data?.incomplete || 0)],
    ]);

    setExtraDataForPie({
      "No. of Completed": data?.noOfCompleted || 0,
      "No. of Incomplete": data?.noOfIncomplete || 0,
    });

    const targetElement = e.currentTarget;
    const rect = targetElement.getBoundingClientRect();

    setHoveredRowStyle({
      top: rect.top + window.scrollY - 350,
      left: rect.left + window.scrollX
    });
  } else {
    setHoveredRowStyle(null);
  }
};

const DrillDownV2: React.FC<DrillDownV2Props> = ({ 
  total, 
  filters,
  fetchMainData,
  fetchSubData,
  drilldownTypes = [],
  onRowHover = defaultOnRowHover,
  exportFilenamePrefix = 'drilldown-export',
  enableHoverPieChart = true
}) => {
  const canExpandRows = Boolean(fetchSubData);
  // Derive drillTypes and idFieldConfig from drilldownTypes
  const drillTypes = useMemo(() => 
    drilldownTypes.map(({ label, value }) => ({ label, value })),
    [drilldownTypes]
  );
  
  const idFieldConfig = useMemo(() => {
    const config: Record<string, { fieldName: string; paramName: string; typeValue: string }> = {};
    drilldownTypes.forEach(({ value, fieldName, paramName, typeValue }) => {
      config[value] = { fieldName, paramName, typeValue };
    });
    return config;
  }, [drilldownTypes]);
  const [showData, setShowData] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false); 
  const [isTrayOpen, setIsTrayOpen] = useState(false); 
  const [showTotalDropdown, setShowTotalDropdown] = useState(false); 
  const [data, setData] = useState<DataType>({ total, subItems: [] });
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [hoveredRowStyle, setHoveredRowStyle] = useState<{top?: number, left?: number} | null>(null);
  const [selectedDataRowForPie, setSelectedDataRowForPie] = useState(selectedDataRowForPieDefaultState);
  const [extraDataForPie, setExtraDataForPie] = useState<Record<string, number | string>>({ total });
  
  const [currentDrillType, setCurrentDrillType] = useState<string | null>(null);

  // Loading state for subquery data
  const [loadingSubData, setLoadingSubData] = useState<Record<string, boolean>>({});

  // Export rows state
  const [isExportMode, setIsExportMode] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);
  
  // AbortController for request cancellation
  const abortControllerRef = useRef<AbortController | null>(null);
  // AbortController for export cancellation
  const exportAbortControllerRef = useRef<AbortController | null>(null);
  // Track items currently being fetched to prevent duplicate requests
  const fetchingItemsRef = useRef<Set<string>>(new Set());
  // Track if export should continue
  const shouldContinueExportRef = useRef<boolean>(true);

  // Sync total prop with state
  useEffect(() => {
    setData(prev => ({ ...prev, total }));
    setExtraDataForPie(prev => ({ ...prev, total }));
  }, [total]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (exportAbortControllerRef.current) {
        exportAbortControllerRef.current.abort();
      }
    };
  }, []);

  // Cancel export when export mode is disabled, tray is closed, or data is hidden
  useEffect(() => {
    if (!isExportMode || !isTrayOpen || !showData) {
      if (exportAbortControllerRef.current) {
        exportAbortControllerRef.current.abort();
        exportAbortControllerRef.current = null;
      }
      shouldContinueExportRef.current = false;
      setIsExporting(false);
      setExportProgress(0);
    } else {
      shouldContinueExportRef.current = true;
    }
  }, [isExportMode, isTrayOpen, showData]);

  const fetchData = useCallback(async (type: string, signal?: AbortSignal) => {
    try {
      const response = await fetchMainData({ 
        type, 
        ...filters
      }, signal);
      
      if (!signal?.aborted) {
        setData((prevS) =>({ ...prevS, subItems: response.data }));
      }
      
    } catch (err) {
      if (!signal?.aborted) {
        console.error("Error fetching dashboard data:", err);
      }
    }
  }, [filters, fetchMainData]);

  const onSelectDrillType = useCallback((type: string) => {
    if (total === 0) return;
    
    // Cancel previous request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController
    abortControllerRef.current = new AbortController();
    
    setShowTotalDropdown(false);
    setIsDataLoading(true);
    setIsTrayOpen(true);
    setCurrentDrillType(type);
    
    fetchData(type, abortControllerRef.current.signal)
      .then(() => {
        if (!abortControllerRef.current?.signal.aborted) {
          setShowData(true);
          setIsDataLoading(false);
        }
      })
      .catch(() => {
        if (!abortControllerRef.current?.signal.aborted) {
          setIsDataLoading(false);
        }
      });
  }, [total, fetchData]);

  const fetchSubDataForItem = useCallback(async (
    item: SubItemWithFields, 
    itemName: string,
    signal?: AbortSignal
  ): Promise<SubItemType[] | null> => {
    if (!fetchSubData) {
      return null;
    }
    // Check if already loading
    if (fetchingItemsRef.current.has(itemName)) return null;
    
    fetchingItemsRef.current.add(itemName);
    setLoadingSubData(prev => ({ ...prev, [itemName]: true }));
    
    try {
      if (!currentDrillType || !idFieldConfig[currentDrillType]) {
        setLoadingSubData(prev => ({ ...prev, [itemName]: false }));
        return null;
      }

      const config = idFieldConfig[currentDrillType];
      const parentId = item[config.fieldName];
      
      if (!parentId) {
        setLoadingSubData(prev => ({ ...prev, [itemName]: false }));
        return null;
      }
      
      const subDataRes = await fetchSubData({
        [config.paramName]: parentId,
        parentType: config.typeValue,
        ...filters
      }, signal);
      
      if (!signal?.aborted) {
        // Update the item with subdata
        setData(prevData => ({
          ...prevData,
          subItems: prevData.subItems.map(subItem => 
            subItem.name === itemName 
              ? { ...subItem, countedFields: subDataRes.data }
              : subItem
          )
        }));
      }
      
      return subDataRes.data;
    } catch (err) {
      if (!signal?.aborted) {
        console.error("Error fetching sub data:", err);
      }
      return null;
    } finally {
      fetchingItemsRef.current.delete(itemName);
      setLoadingSubData(prev => ({ ...prev, [itemName]: false }));
    }
  }, [currentDrillType, filters, fetchSubData, idFieldConfig]);

  const toggleExpand = useCallback((item: SubItemWithFields, itemName: string) => {
    if (!fetchSubData) {
      return;
    }
    const isExpanded = expanded[itemName];
    
    if (!isExpanded && !item.countedFields) {
      // First time expanding - fetch sub data
      fetchSubDataForItem(item, itemName);
    }
    
    setExpanded(prev => ({ ...prev, [itemName]: !prev[itemName] }));
  }, [expanded, fetchSubData, fetchSubDataForItem]);

  const toggleRowSelection = useCallback((rowName: string) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowName)) {
        newSet.delete(rowName);
      } else {
        newSet.add(rowName);
      }
      return newSet;
    });
  }, []);

  const allRowNames = useMemo(() => 
    data.subItems.map(item => item.name), 
    [data.subItems]
  );

  const toggleSelectAll = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    
    const willBeChecked = e.target.checked;
    
    if (willBeChecked) {
      setSelectedRows(new Set(allRowNames));
    } else {
      setSelectedRows(new Set());
    }
  }, [allRowNames]);

  const chartOptions = useMemo(() => ({
    ...dashboardGraphsOptions.PieChartOptions,
    colors: ['#FFCC99', '#336699'],
    legend: {...dashboardGraphsOptions.PieChartOptions.legend, position: "bottom" as const },
  }), []);

  const isAllSelected = useMemo(() => 
    data.subItems.length > 0 && selectedRows.size === data.subItems.length,
    [data.subItems.length, selectedRows.size]
  );

  const exportButtonStyle = useMemo(() => ({
    ...styles.exportButtonStyle,
    backgroundColor: (selectedRows.size > 0 && !isExporting) ? '#08305D' : '#ccc',
    cursor: (selectedRows.size > 0 && !isExporting) ? 'pointer' : 'not-allowed',
  }), [selectedRows.size, isExporting]);

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      const someSelected = selectedRows.size > 0 && selectedRows.size < data.subItems.length;
      selectAllCheckboxRef.current.indeterminate = someSelected;
    }
  }, [selectedRows, data.subItems.length]);

  const handleExportSelected = useCallback(async () => {
    if (selectedRows.size === 0) {
      alert('Please select at least one row to export');
      return;
    }

    // Cancel any existing export
    if (exportAbortControllerRef.current) {
      exportAbortControllerRef.current.abort();
    }

    // Create new AbortController for this export
    exportAbortControllerRef.current = new AbortController();
    shouldContinueExportRef.current = true;

    setIsExporting(true);
    setExportProgress(0);

    try {
      // Check if export should continue before starting
      if (!shouldContinueExportRef.current || !isExportMode || !isTrayOpen || !showData) {
        return;
      }

      const selectedItems = data.subItems.filter(item => selectedRows.has(item.name));
      const itemsNeedingSubdata = fetchSubData
        ? selectedItems.filter(item => !item.countedFields && item.total > 0)
        : [];

      const itemSubdataMap = new Map<string, SubItemType[]>();

      // Calculate total steps for progress tracking
      const totalSteps = itemsNeedingSubdata.length > 0 
        ? itemsNeedingSubdata.length + selectedItems.length 
        : selectedItems.length;
      const fetchPhaseWeight = itemsNeedingSubdata.length > 0 ? 0.6 : 0;
      const buildPhaseWeight = 1 - fetchPhaseWeight;

      // Fetch subdata for items that need it
      if (itemsNeedingSubdata.length > 0) {
        for (let i = 0; i < itemsNeedingSubdata.length; i++) {
          if (!shouldContinueExportRef.current || !isExportMode || !isTrayOpen || !showData || exportAbortControllerRef.current?.signal.aborted) {
            return;
          }

          const item = itemsNeedingSubdata[i];
          try {
            if (fetchSubData) {
              setExpanded(prev => ({ ...prev, [item.name]: true }));
            }
            
            const fetchProgress = ((i + 1) / itemsNeedingSubdata.length) * fetchPhaseWeight * 100;
            setExportProgress(Math.round(fetchProgress));
            
            const subItems = await fetchSubDataForItem(item, item.name, exportAbortControllerRef.current?.signal);
            
            if (!shouldContinueExportRef.current || !isExportMode || !isTrayOpen || !showData || exportAbortControllerRef.current?.signal.aborted) {
              return;
            }
            
            if (subItems) {
              itemSubdataMap.set(item.name, subItems);
            }
          } catch (err) {
            if (exportAbortControllerRef.current?.signal.aborted) {
              return;
            }
            console.error(`Error loading subdata for ${item.name}:`, err);
          }
        }
      }

      // Check if export should continue before building CSV
      if (!shouldContinueExportRef.current || !isExportMode || !isTrayOpen || !showData || exportAbortControllerRef.current?.signal.aborted) {
        return;
      }

      const csvData: (string | number)[][] = [];
      
      csvData.push([
        'Name',
        'Complete %',
        'No. of Completed',
        'Incomplete %',
        'No. of Incomplete',
        'Total'
      ]);

      // Build CSV data with progress tracking
      for (let i = 0; i < selectedItems.length; i++) {
        if (!shouldContinueExportRef.current || !isExportMode || !isTrayOpen || !showData || exportAbortControllerRef.current?.signal.aborted) {
          return;
        }

        const item = selectedItems[i];
        csvData.push([
          item.name,
          item.completed,
          item.noOfCompleted,
          item.incomplete,
          item.noOfIncomplete,
          item.total
        ]);

        const subItems = itemSubdataMap.get(item.name) || item.countedFields;

        if (subItems && subItems.length > 0) {
          subItems.forEach((subItem) => {
            csvData.push([
              `  ${subItem.name}`, 
              subItem.completed,
              subItem.noOfCompleted,
              subItem.incomplete,
              subItem.noOfIncomplete,
              subItem.total,
            ]);
          });
        }

        const buildProgress = fetchPhaseWeight * 100 + ((i + 1) / selectedItems.length) * buildPhaseWeight * 100;
        setExportProgress(Math.round(buildProgress));
      }

      // Final check before download
      if (!shouldContinueExportRef.current || !isExportMode || !isTrayOpen || !showData || exportAbortControllerRef.current?.signal.aborted) {
        return;
      }

      setExportProgress(100);
      
      downloadCSV(csvData, `${exportFilenamePrefix}-${Date.now()}`);
      toast.success(`Successfully exported ${selectedItems.length} item(s) to CSV`);
      setIsExportMode(false);
      setSelectedRows(new Set());
      setExportProgress(0);
    } catch (error) {
      if (exportAbortControllerRef.current?.signal.aborted) {
        return;
      }
      console.error('Error during export:', error);
      alert('An error occurred during export. Please try again.');
      setExportProgress(0);
    } finally {
      setIsExporting(false);
      exportAbortControllerRef.current = null;
      shouldContinueExportRef.current = true;
    }
  }, [selectedRows, data.subItems, fetchSubData, fetchSubDataForItem, isExportMode, isTrayOpen, showData, exportFilenamePrefix]);

  const handleToggleExportMode = useCallback(() => {
    setIsExportMode(prev => {
      const newValue = !prev;
      if (!newValue && exportAbortControllerRef.current) {
        exportAbortControllerRef.current.abort();
        exportAbortControllerRef.current = null;
        shouldContinueExportRef.current = false;
        setIsExporting(false);
        setExportProgress(0);
      }
      return newValue;
    });
  }, []);

  const handleTogglePicker = useCallback(() => {
    if (total === 0) return;
    changeDrillDownPickerPosition(
      showData,
      setShowData,
      setShowTotalDropdown,
      setIsExportMode,
      setSelectedRows,
    );
  }, [total, showData]);

  return <>
    <div className="expandable-list-total">
      <span>Total</span>
      <span>{data?.total}</span>
      
      <div style={styles.buttonContainer}>
        {showData && (
          <button 
            className="drill-down-picker"
            onClick={handleToggleExportMode}
            style={styles.exportButton}
          >
            Export rows
          </button>
        )}
        <button 
          className="drill-down-picker"
          onClick={handleTogglePicker}
        >
          {showData ? <X size={16} /> : <Plus size={16} />}
        </button>
      </div>
      {showTotalDropdown && (
        <div className="chart-hovered dropdown-hover">
          {drillTypes.map((drillType) => (
            <div
              key={drillType.value}
              style={styles.dropdownItem}
              onClick={() => onSelectDrillType(drillType.value)}
            >
              {drillType.label}
            </div>
          ))}
        </div>
      )}
    </div>
    {isTrayOpen && <div 
      className="expandable-list chart-hovered-with-pie"
      style={showData ? styles.trayContainerWithPadding : styles.trayContainer}
    >
      {enableHoverPieChart && hoveredRowStyle !== null && (
        <div className='chart-hovered' style={styles.hoveredChart}>
          <div className='chart-hovered-buttons'>
            <button
              className="close-button"
              about="tools"
              onClick={() => setHoveredRowStyle(null)}
              style={styles.closeButton}
            />
          </div>
          <div className="headings">
            {Object.keys(extraDataForPie).map(dt => <div key={dt}>{dt} <b>{extraDataForPie[dt]}</b></div>)}
          </div>
          <Chart
            width="320px"
            height="200px"
            chartType="PieChart"
            data={selectedDataRowForPie}
            options={chartOptions}
          />
        </div>
      )}
      {
        showData ? (
          <>
            {isExportMode && (
              <div className="category" style={styles.exportHeader}>
                <label style={styles.checkboxLabel}>
                  <input
                    ref={selectAllCheckboxRef}
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={toggleSelectAll}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    style={styles.checkbox}
                  />
                  <span>Select all</span>
                </label>
                <button
                  onClick={handleExportSelected}
                  disabled={selectedRows.size === 0 || isExporting}
                  style={exportButtonStyle}
                >
                  {isExporting ? `Exporting ${exportProgress}%` : 'Export'}
                </button>
              </div>
            )}
            {data.subItems?.map((item) => {
              const { name, completed, noOfCompleted, incomplete, noOfIncomplete, total, countedFields } = item;
              // Create unique key using name and any ID fields
              const idFields = currentDrillType && idFieldConfig[currentDrillType] 
                ? item[idFieldConfig[currentDrillType].fieldName] 
                : name;
              const uniqueKey = `${name}-${idFields || name}`;
              
              return (
                <div key={uniqueKey}>
                  <div
                    className="category"
                    onClick={(e) => {
                      if (total === 0) return;
                      if (isExportMode && (e.target as HTMLElement).tagName === 'INPUT') {
                        return;
                      }
                      if (!canExpandRows) {
                        return;
                      }
                      toggleExpand(item, name);
                    }}
                  >
                    <div className="category-header" style={styles.categoryHeader}>
                      {isExportMode && (
                        <input
                          type="checkbox"
                          checked={selectedRows.has(name)}
                          onChange={() => toggleRowSelection(name)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ cursor: 'pointer' }}
                        />
                      )}
                      {canExpandRows && total > 0 && (
                        (expanded[name] ? <ChevronDown size={16} /> : <ChevronRight size={16} />)
                      )}
                      <span
                        onMouseEnter={(e) => {
                          if (!isExportMode && enableHoverPieChart) {
                            onRowHover(
                              e,
                              { completed, noOfCompleted, incomplete, noOfIncomplete, total },
                              setHoveredRowStyle,
                              setSelectedDataRowForPie,
                              setExtraDataForPie,
                            );
                          }
                        }}
                        onMouseLeave={() => {
                          if (!isExportMode && enableHoverPieChart) {
                            setHoveredRowStyle(null);
                          }
                        }}
                      >{name}</span>
                    </div>
                    <span>{total}</span>
                  </div>
                  {expanded[name] && (
                    <div className="sub-items">
                      {loadingSubData[name] ? (
                        <div style={styles.loadingSubItem}>
                          Loading...
                        </div>
                      ) : (
                        countedFields?.map((subItem, subIdx) => {
                          const subItemKey = `${uniqueKey}-sub-${subItem.name}-${subIdx}`;
                          
                          return (
                            <div
                              key={subItemKey}
                              className="sub-item"
                            >
                              <span
                                onMouseEnter={(e) => {
                                  if (enableHoverPieChart) {
                                    onRowHover(
                                      e,
                                      {
                                        completed: subItem.completed,
                                        noOfCompleted: subItem.noOfCompleted,
                                        incomplete: subItem.incomplete,
                                        noOfIncomplete: subItem.noOfIncomplete,
                                        total: subItem.total,
                                      },
                                      setHoveredRowStyle,
                                      setSelectedDataRowForPie,
                                      setExtraDataForPie,
                                    );
                                  }
                                }}
                                onMouseLeave={() => {
                                  if (enableHoverPieChart) {
                                    setHoveredRowStyle(null);
                                  }
                                }}
                              >{subItem.name}</span>
                              <span>{subItem.total}</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        ) : <AppLoading isLoading={isDataLoading} style={{ height: "250px" }}/>}
    </div>
    }
  </>
};

export default DrillDownV2;

const changeDrillDownPickerPosition = (
  showData: boolean,
  setShowData: (value: React.SetStateAction<boolean>) => void,
  setShowTotalDropdown: (value: React.SetStateAction<boolean>) => void,
  setIsExportMode?: (value: React.SetStateAction<boolean>) => void,
  setSelectedRows?: (value: React.SetStateAction<Set<string>>) => void,
) => {
  setShowData(false);
  setShowTotalDropdown(!showData);
  if (setIsExportMode) setIsExportMode(false);
  if (setSelectedRows) setSelectedRows(new Set());
}

